/**
 * Auth queries — authentication, profiles, referrals, and session management.
 *
 * Centralizes all auth-related Supabase calls. Any change to auth tables,
 * columns, or logic should only need to be updated here.
 *
 * Used by: AuthContext, ResetPassword page
 */
import { supabase } from '@/lib/supabase'
import { invokeEdgeJson } from './_edge'
import { updateProfileField } from './_helpers'
import { EXPORT_USER_DATA } from './_endpoints'

// ── Session Management ──────────────────────────────────────────

/** Get the current auth session (JWT + user). */
export async function getSession() {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession()
  if (error) throw error
  return session
}

/** Refresh the current JWT. Returns fresh session or null. */
export async function refreshSession() {
  const {
    data: { session },
    error,
  } = await supabase.auth.refreshSession()
  if (error) throw error
  return session
}

/**
 * Get a valid session, refreshing if needed.
 * Replaces duplicated getSession+refreshSession pattern in 3 contexts.
 * @returns {Promise<{accessToken: string, userId: string}>}
 */
export async function getValidSession() {
  let session = await getSession()
  if (!session) {
    session = await refreshSession()
  }
  if (!session) throw new Error('Not authenticated')
  return {
    accessToken: session.access_token,
    userId: session.user.id,
  }
}

// ── Auth Actions ────────────────────────────────────────────────

/**
 * Sign up with email/password. Optional `metadata` is passed through as
 * `options.data` for the handle_new_user trigger. `emailRedirectTo` controls
 * where Supabase sends the user after they click the confirmation link in
 * their email — defaults to `/onboarding` so fresh signups land in the
 * activation funnel, not the marketing home page.
 */
export async function signUp(email, password, metadata, emailRedirectTo) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      ...(metadata && { data: metadata }),
      ...(emailRedirectTo && { emailRedirectTo }),
    },
  })
  if (error) throw error
  return data
}

/** Sign in with email/password. */
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

/** Sign in with Google OAuth. */
export async function signInWithGoogle(redirectTo) {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo },
  })
  if (error) throw error
  return data
}

/** Send password reset email. */
export async function resetPassword(email, redirectTo) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo })
  if (error) throw error
}

/** Update user password (after reset). */
export async function updatePassword(password) {
  const { error } = await supabase.auth.updateUser({ password })
  if (error) throw error
}

/** Sign out the current user. */
export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

/** Listen for auth state changes. Returns unsubscribe function. */
export function onAuthStateChange(callback) {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange(callback)
  return subscription
}

// ── Profiles ────────────────────────────────────────────────────

/** Fetch a user's profile by ID. Returns null if no row exists (PGRST116). */
export async function fetchProfile(userId) {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single()
  if (error?.code === 'PGRST116') return null
  if (error) throw error
  return data
}

/** Upsert (create or update) a user's profile. Stamps updated_at automatically. */
export async function upsertProfile(userId, data) {
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, ...data, updated_at: new Date().toISOString() })
  if (error) throw error
}

// ── Personalization "last actual use" writers ───────────────────
// These back the Onboarding Personalization v1 "last actual use wins"
// decay model. See src/lib/personalization.js and specs history.

/** Persist the chat tab the user last switched to. */
export async function updateLastChatTab(userId, tab) {
  await updateProfileField(userId, { last_chat_tab: tab })
}

/** Persist the Bible book + chapter the user last opened. */
export async function updateLastBibleLocation(userId, book, chapter) {
  await updateProfileField(userId, { last_bible_book: book, last_bible_chapter: chapter })
}

/**
 * Stamp the first Home Dashboard visit so card personalization only
 * applies once. Idempotent — skips the write if the column is already set,
 * so this stays a custom query (the shared updateProfileField helper does
 * not support the `.is(col, null)` filter).
 */
export async function markHomeFirstVisit(userId) {
  const { error } = await supabase
    .from('profiles')
    .update({ home_dashboard_first_visit_at: new Date().toISOString() })
    .eq('id', userId)
    .is('home_dashboard_first_visit_at', null)
  if (error) throw error
}

/** Stamp the onboarding-completion "next step" card as seen. */
export async function markOnboardingNextStepSeen(userId) {
  await updateProfileField(userId, { onboarding_next_step_seen_at: new Date().toISOString() })
}

// ── Referrals ───────────────────────────────────────────────────

/** Look up a referral code. Returns profile ID if valid, null otherwise. */
export async function lookupReferralCode(code) {
  const { data, error } = await supabase
    .from('profiles')
    .select('id')
    .eq('referral_code', code)
    .single()
  // PGRST116 = no rows found — expected when code is invalid
  if (error && error.code !== 'PGRST116') throw error
  return data?.id || null
}

// ── Invite Codes ────────────────────────────────────────────────

/**
 * Redeem an invite code via the atomic Postgres RPC.
 * Returns `{ success, reason?, group_name? }`. The RPC handles expiry check
 * (using server `now()`), use-count cap, per-user prior-redemption check, and
 * the profile + redemption-row writes in a single transaction.
 *
 * RPC name `redeem_beta_invite` is retained for stability — a future planned
 * migration will rename it (see `rename_beta_db_objects` in roadmapData.js).
 */
export async function redeemInviteCode(inviteCode) {
  const { data, error } = await supabase.rpc('redeem_beta_invite', {
    invite_code: inviteCode,
  })
  if (error) throw error
  return data || { success: false, reason: 'invalid' }
}

// ── Data Export ────────────────────────────────────────────────

/**
 * Returns the current user's exported data as a JS object via the
 * export-user-data edge function. The caller handles the download
 * (see downloadAsJson in src/lib/download.js).
 */
export async function exportUserData() {
  const data = await invokeEdgeJson(EXPORT_USER_DATA, {}, { errorMessage: 'Data export failed' })
  return data
}
